import * as React from "react";
import type { CollaborateAgreementVersion } from "../types/collaborateThread";
import type { CollaborateSelectionContext } from "../types/selection";
import { useCollaborateStore } from "../store/useCollaborateStore";
import { useOrderedThreads } from "../hooks/useCollaborateSelectors";
import { useCollaborateInfiniteScroll } from "../hooks/useCollaborateInfiniteScroll";
import ThreadRow from "./ThreadRow";
import CollabSpinner from "./ui/CollabSpinner";

export type CollaborateThreadListProps = {
  selectedContext: CollaborateSelectionContext | null;
  onSelectionConsumed?: () => void;
  documentId?: string;
  versions?: CollaborateAgreementVersion[];
  defaultComposerVersionId?: string | null;
  currentVersionId?: string | null;
};

const CollaborateThreadList: React.FC<CollaborateThreadListProps> = ({
  documentId,
  currentVersionId = null,
  versions = [],
  defaultComposerVersionId = null,
}) => {
  const loading = useCollaborateStore((s) => s.loading);
  const hydrated = useCollaborateStore((s) => s.hydrated);
  const threads = useOrderedThreads();
  const listView = useCollaborateStore((s) => s.listView);
  const versionFilter = useCollaborateStore((s) => s.versionFilter);
  const { loadingInitial, loadingMore, total } = useCollaborateStore((s) => s.threadsPagination);
  const focusThread = useCollaborateStore((s) => s.focusThread);

  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const sentinelRef = useCollaborateInfiniteScroll(
    scrollRef,
    documentId,
    `${listView}-${versionFilter ?? "all"}`,
  );

  const isCommentsView = listView === "comments";

  if ((loading || loadingInitial) && !hydrated) {
    return (
      <div className="collab-center">
        <CollabSpinner label="Loading threads" />
      </div>
    );
  }

  return (
    <div className="collaborate-thread-list-shell">
      <div ref={scrollRef} className="collaborate-pane__scroll thin-scroll">
        {(loading || loadingInitial) && hydrated ? (
          <div className="collaborate-thread-list-loader collaborate-thread-list-loader--overlay">
            <CollabSpinner size="sm" />
          </div>
        ) : null}

        {threads.length === 0 && total === 0 && !isCommentsView && !loadingInitial ? (
          <div className="collab-empty">Nothing here.</div>
        ) : null}

        {threads.length === 0 && total > 0 && !loadingInitial ? (
          <div className="collab-empty">Nothing here.</div>
        ) : null}

        {threads.length === 0 && isCommentsView && !loadingInitial && total === 0 ? (
          <div className="collab-empty">No comments yet. Start the conversation above.</div>
        ) : null}

        {threads.map((t) => (
          <ThreadRow
            key={t.id}
            thread={t}
            onClick={() => void focusThread(t.id, currentVersionId ?? "")}
            currentVersionId={currentVersionId}
            versions={versions}
            defaultComposerVersionId={defaultComposerVersionId}
          />
        ))}

        {loadingMore ? (
          <div className="collaborate-thread-list-loader">
            <CollabSpinner size="sm" />
          </div>
        ) : null}

        <div ref={sentinelRef} className="collaborate-thread-list-sentinel" aria-hidden />
      </div>
    </div>
  );
};

export default CollaborateThreadList;
